/**
 * @fileoverview Asset Quality page — Sector-wise NPA.
 * @description Ported from the InsightBox Banking design system's own
 * `AssetQuality/SectorWiseNpa.tsx`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchLabel,
  core,
  formatLacs,
  npaSectors,
  resolveBankingAccessScope,
  resolveFy,
  totalNpaAmountLacs,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import RankedBarList from '@/shared/components/molecules/RankedBarList'
import { severityColor } from '@/shared/constants/chart-theme.constants'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'
import type { RankedBarListItem } from '@/shared/components/molecules/RankedBarList'

/**
 * @component
 */
function BankingAssetQualityPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.assetQuality.kicker')}
      title={t('banking.assetQuality.title')}
      description={t('banking.assetQuality.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const sectors = npaSectors(data, fy, branchId)
  const totalAmount = totalNpaAmountLacs(data, fy, branchId)
  const totalAccounts = sectors.reduce((sum, s) => sum + s.accounts, 0)
  const advances = core(data, fy, branchId).advances
  const overallPct = (totalAmount / 100 / advances) * 100
  const top = sectors[0]!
  const personalSector = sectors.find((s) => s.key === 'personal')

  const chartItems: RankedBarListItem[] = sectors.map((s) => ({
    key: s.key,
    label: t('banking.assetQuality.sectorLabel', {
      label: s.label,
      accounts: s.accounts,
      accountsUnit:
        s.accounts === 1
          ? t('banking.assetQuality.accountUnit')
          : t('banking.assetQuality.accountsUnit'),
    }),
    value: s.npaPct,
    displayValue: `${formatLacs(s.amountLacs)} / ${s.npaPct.toFixed(2)}%`,
    color: severityColor(s.npaPct),
  }))

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.assetQuality.kpi.totalNpaAmount'),
      value: formatLacs(totalAmount),
      sublabel: t('banking.assetQuality.kpi.acrossAllSectors'),
      highlight: true,
    },
    {
      label: t('banking.assetQuality.kpi.totalNpaAccounts'),
      value: String(totalAccounts),
      sublabel: t('banking.assetQuality.kpi.acrossAllSectors'),
    },
    {
      label: t('banking.assetQuality.kpi.overallNpaPct'),
      value: `${overallPct.toFixed(2)}%`,
      sublabel: t('banking.assetQuality.kpi.ofTotalAdvances'),
    },
    {
      label: t('banking.assetQuality.kpi.topNpaSector'),
      value: top.label,
      sublabel: t('banking.assetQuality.kpi.npaPctSuffix', { pct: top.npaPct.toFixed(2) }),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={t('banking.assetQuality.chartTitle')}
        subtitle={t('banking.assetQuality.chartSubtitle', { branch: branchLabel(branchId), fy })}
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          <RankedBarList items={chartItems} labelWidth={190} />
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={[
          t('banking.assetQuality.summary.line1', {
            sector1: sectors[0]!.label,
            pct1: sectors[0]!.npaPct.toFixed(2),
            sector2: sectors[1]!.label,
            pct2: sectors[1]!.npaPct.toFixed(2),
            sector3: sectors[2]!.label,
            pct3: sectors[2]!.npaPct.toFixed(2),
          }),
          t('banking.assetQuality.summary.line2', {
            accounts: personalSector?.accounts ?? 0,
            sector1: sectors[0]!.label,
            sector2: sectors[1]!.label,
            sector3: sectors[2]!.label,
          }),
        ]}
      />
    </div>
  )
}

export default BankingAssetQualityPage
